import React from "react";

/*
 * The canonical Audience Data Synthesis body — extracted so that every surface
 * which presents a governed audience record presents it the SAME way.
 *
 * This is the hierarchy the individual Platform Analytics pages have always
 * rendered (executive read, signal cards, interpretation, geography, segments,
 * recommendations, analysis). It moved out of AudienceSynthesisPanel unchanged
 * so the Cross-Platform module could stop flattening the same governed record
 * into a single paragraph. There is one renderer, not a second design.
 *
 * It is purely presentational and takes a report already built by
 * buildAudienceIntelligenceReport: it never fetches, never rewrites, and never
 * emits a field the canonical record does not carry. Every string it prints is
 * a verbatim span of the governed record.
 *
 * Class names stay in the `ppx-audience-` namespace on every surface. The rules
 * live in PlatformAnalyticsExperience.css, which is fully namespaced, so a page
 * that imports it inherits the canonical typography and layout rather than
 * restating them — restating is how the two presentations drifted apart.
 */

function SignalCard({ signal }) {
  return (
    <li className="ppx-audience-signal">
      <h4 className="ppx-audience-signal-label">{signal.label}</h4>
      <p className="ppx-audience-signal-takeaway">{signal.takeaway}</p>
      {signal.evidence.length > 0 && (
        <div className="ppx-audience-signal-evidence">
          {signal.evidence.map((line, index) => (
            <p key={index}>{line}</p>
          ))}
        </div>
      )}
    </li>
  );
}

/*
 * `headingLevel` exists because this body is a top-level section on a platform
 * page but nests under a per-platform heading in the Cross-Platform module.
 * Only the heading RANK changes — never the content, the order, or the labels.
 */
export default function AudienceSynthesisReport({ report, headingLevel = 3 }) {
  if (!report) return null;

  const hasBody =
    report.executiveRead.headline ||
    report.signals.length > 0 ||
    report.interpretation.modes.length > 0 ||
    report.interpretation.prose.length > 0 ||
    report.recommendations.length > 0 ||
    report.analysis.length > 0;

  if (!hasBody) return null;

  const SectionHeading = `h${Math.min(Math.max(headingLevel, 2), 5)}`;
  const SubHeading = `h${Math.min(Math.max(headingLevel + 1, 3), 6)}`;

  return (
    <div className="ppx-audience-report">
      {report.executiveRead.headline && (
        <section className="ppx-audience-executive" aria-label="Executive audience read">
          <SectionHeading className="ppx-audience-section-label">
            Executive Audience Read
          </SectionHeading>
          <p className="ppx-audience-executive-headline">{report.executiveRead.headline}</p>
          {report.executiveRead.coverage.length > 0 && (
            <ul className="ppx-audience-coverage" aria-label="Signals covered by this synthesis">
              {report.executiveRead.coverage.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {report.signals.length > 0 && (
        <section aria-label="Key audience signals">
          <SectionHeading className="ppx-audience-section-label">
            Key Audience Signals
          </SectionHeading>
          <ul className="ppx-audience-signal-grid">
            {report.signals.map((signal) => (
              <SignalCard key={signal.key} signal={signal} />
            ))}
          </ul>
        </section>
      )}

      {(report.interpretation.modes.length > 0 || report.interpretation.prose.length > 0) && (
        <section aria-label="Audience behavior and interpretation">
          <SectionHeading className="ppx-audience-section-label">
            Audience Behavior · Interpretation
          </SectionHeading>
          {report.interpretation.modes.length > 0 && (
            <ul className="ppx-audience-mode-grid">
              {report.interpretation.modes.map((mode, index) => (
                <li className="ppx-audience-mode" key={index}>
                  <SubHeading className="ppx-audience-mode-label">{mode.label}</SubHeading>
                  <p className="ppx-audience-mode-body">{mode.body}</p>
                </li>
              ))}
            </ul>
          )}
          {report.interpretation.prose.length > 0 && (
            <div className="ppx-audience-flow">
              {report.interpretation.prose.map((line, index) => (
                <p className="ppx-audience-prose" key={index}>
                  {line}
                </p>
              ))}
            </div>
          )}
        </section>
      )}

      {report.geography && (
        <section className="ppx-audience-geography" aria-label="Geographic signals">
          <SectionHeading className="ppx-audience-section-label">
            Geographic Signals
          </SectionHeading>
          <div className="ppx-audience-flow">
            <p className="ppx-audience-prose">{report.geography.takeaway}</p>
            {report.geography.evidence.map((line, index) => (
              <p className="ppx-audience-prose" key={index}>
                {line}
              </p>
            ))}
          </div>
          {report.geography.caution && (
            <p className="ppx-audience-geography-caution" role="note">
              {report.geography.caution}
            </p>
          )}
        </section>
      )}

      {(report.strongestSegments.length > 0 || report.emergingSegments.length > 0) && (
        <section aria-label="Audience segments">
          <SectionHeading className="ppx-audience-section-label">Segments</SectionHeading>
          <div className="ppx-audience-segment-groups">
            {report.strongestSegments.length > 0 && (
              <div>
                <SubHeading className="ppx-audience-signal-label">Strongest</SubHeading>
                <ul className="ppx-audience-bullets">
                  {report.strongestSegments.map((segment, index) => (
                    <li key={index}>{segment}</li>
                  ))}
                </ul>
              </div>
            )}
            {report.emergingSegments.length > 0 && (
              <div>
                <SubHeading className="ppx-audience-signal-label">Emerging</SubHeading>
                <ul className="ppx-audience-bullets">
                  {report.emergingSegments.map((segment, index) => (
                    <li key={index}>{segment}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {report.recommendations.length > 0 && (
        <section className="ppx-audience-recommendations" aria-label="Recommended actions">
          <SectionHeading className="ppx-audience-section-label">
            Recommended Actions
          </SectionHeading>
          <ol className="ppx-audience-recommendation-list">
            {report.recommendations.map((entry, index) => (
              <li key={index}>{entry}</li>
            ))}
          </ol>
          <p className="ppx-audience-recommendation-note" role="note">
            Read-only guidance carried by the canonical record. Nothing here changes a campaign.
          </p>
        </section>
      )}

      {report.analysis.length > 0 && (
        <section aria-label="Full analysis">
          <SectionHeading className="ppx-audience-section-label">Analysis</SectionHeading>
          <div className="ppx-audience-flow">
            {report.analysis.map((paragraph, index) => (
              <p className="ppx-audience-prose" key={index}>
                {paragraph}
              </p>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
